<i18n>
{
  "no": {
    "errors": {
      "badInputName": "Minst én bokstav",
      "badNin": "11 siffer påkrevd"
    },
    "firstAndMiddleName": "For- og mellomnavn",
    "yourFirstAndMiddleName": "Ditt for- og mellomavn",
    "lastName": "Etternavn",
    "yourLastName": "Ditt etternavn",
    "nationalIdentityNumber": "Fødselsnummer",
    "11digitNumber": "Ditt fødselsnummer (11 siffer)",
    "why": "Hvorfor?"
  },
  "en": {
    "errors": {
      "badInputName": "At least one character",
      "badNin": "11 digits"
    },
    "firstAndMiddleName": "First and middle name",
    "yourFirstAndMiddleName": "Your first and middle names",
    "lastName": "Last name",
    "yourLastName": "Your last name",
    "nationalIdentityNumber": "National ID number",
    "11digitNumber": "Your national ID number (11 digits)",
    "why": "Why?"
  }
}
</i18n>

<template>
  <div class="field-stack">
    <input type="hidden" name="msisdn" :value="signUpRequest.msisdn" />

    <TnTextField
      id="txtFirstAndMiddleName"
      v-model="firstAndMiddleName"
      name="firstAndMiddleName"
      :label="t('firstAndMiddleName')"
      :placeholder="t('yourFirstAndMiddleName')"
      :required="!signUpRequest.canCancelSignUp"
      :error="
        firstNameTouched && !nameValid(firstAndMiddleName) ? t('errors.badInputName') : undefined
      "
      @blur="firstNameTouched = true"
    />

    <TnTextField
      id="txtLastName"
      v-model="lastName"
      name="lastName"
      :label="t('lastName')"
      :placeholder="t('yourLastName')"
      :required="!signUpRequest.canCancelSignUp"
      :error="lastNameTouched && !nameValid(lastName) ? t('errors.badInputName') : undefined"
      @blur="lastNameTouched = true"
    />

    <div>
      <div class="margin-bottom-2xs label-row">
        <TnLabel :text="t('nationalIdentityNumber')" for-input="txtNationalIdentityNumber" />
        <TnLink size="s" :to="{ name: 'ibis-national-id-info' }" :text="t('why')" />
      </div>

      <TnTextField
        id="txtNationalIdentityNumber"
        v-model="nationalIdentityNumber"
        name="nationalIdentityNumber"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        :placeholder="t('11digitNumber')"
        :required="!signUpRequest.canCancelSignUp"
        :error="ninTouched && !ninValid ? t('errors.badNin') : undefined"
        @blur="ninTouched = true"
      />
    </div>

    <input type="hidden" name="signUpId" :value="signUpRequest.signUpId" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { TnLabel, TnLink, TnTextField } from '@telenornorgeinternal/telegrafen-vue3-core';

import type { SignUpRequestFormFields } from '@/types';

const props = defineProps<{ signUpRequest: SignUpRequestFormFields }>();
const formValid = defineModel<boolean>('formValid', { default: false });

const { t } = useI18n();

const firstAndMiddleName = ref<string>(props.signUpRequest.firstAndMiddleName ?? '');
const lastName = ref<string>(props.signUpRequest.lastName ?? '');
const nationalIdentityNumber = ref<string>(props.signUpRequest.nationalIdentityNumber ?? '');

const firstNameTouched = ref(false);
const lastNameTouched = ref(false);
const ninTouched = ref(false);

function nameValid(value: string) {
  if (!value && props.signUpRequest.canCancelSignUp) return true;
  return Boolean(value) && value.length >= 1;
}

const ninValid = computed(() => {
  if (!nationalIdentityNumber.value && props.signUpRequest.canCancelSignUp) return true;
  return /^\d{11}$/.test(nationalIdentityNumber.value);
});

const allValid = computed(
  () => nameValid(firstAndMiddleName.value) && nameValid(lastName.value) && ninValid.value,
);

watch(allValid, (valid) => (formValid.value = valid), { immediate: true });
</script>

<style scoped>
.label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}
</style>
